/**
 * Kinematics V2 Adapter
 * 
 * Adapter utilities for handling V2 response structure
 */

export interface Tier1MetricV2 {
  key: string;
  name: string;
  value: number;
  unit: string;
  confidence_level: "HIGH" | "MEDIUM" | "LOW";
  tier: 1;
  target_range: { min: number; max: number };
  is_scored: boolean;
  score: number | null;
  status: "IN_RANGE" | "BELOW" | "ABOVE" | "NO_SCORE";
  notes: string[];
}

export interface Tier2MetricV2 {
  key: string;
  name: string;
  value: number;
  unit: string;
  confidence_level: "HIGH" | "MEDIUM" | "LOW";
  tier: 2;
  target_range: { min: number; max: number } | null;
  is_scored: false;
  score: null;
  status: "NO_SCORE";
  notes: string[];
}

export interface KinematicsAnalysisV2 {
  version: "2.0";
  overall_score: number | null;
  cohort: {
    cohort_id: string;
    shot_type: string;
    config_source: "default" | "cohort";
    config_version: string;
  };
  tier1_metrics: Tier1MetricV2[];
  tier2_metrics: Tier2MetricV2[];
  notes: string[];
  legacy?: {
    raw_metrics: Record<string, number>;
  };
}

/**
 * Check if analysis data contains V2 response
 */
export function hasV2Response(data: any): boolean {
  return data?.v2?.version === "2.0" || data?.analysis?.v2?.version === "2.0";
}

/**
 * Extract V2 response from analysis data
 */
export function getV2Response(data: any): KinematicsAnalysisV2 | null {
  if (data?.v2) {
    return data.v2;
  }
  if (data?.analysis?.v2) {
    return data.analysis.v2;
  }
  return null;
}

/**
 * Get overall score from V2 or fallback to legacy
 */
export function getOverallScore(data: any): number | null {
  const v2 = getV2Response(data);
  if (v2) {
    return v2.overall_score;
  }
  // Fallback to legacy
  return data?.analysis?.overallScore ?? data?.analysis?.overall_score ?? null;
}

/**
 * Get scored Tier 1 metrics count
 */
export function getScoredTier1Count(v2: KinematicsAnalysisV2): number {
  return v2.tier1_metrics.filter(m => m.is_scored && m.score !== null).length;
}

/**
 * Check if overall score can be computed (requires 3+ scored Tier 1 metrics)
 */
export function canComputeOverallScore(v2: KinematicsAnalysisV2): boolean {
  return getScoredTier1Count(v2) >= 3;
}
